import { Button } from '@/components/elements/forms/buttons';
import { TERMS_URLS } from '@/constants/terms';
import ListInput from '@/components/elements/forms/listInput/ListInput/ListInput';
import Icon from '@/components/icons/Icon';
import { loginTrackingSession } from '@/helpers/loginTrackingSession';
import { LoginContextType } from '@/hooks/useLogin';
import { _s } from '@/locale';
import { ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

type ProtectedUserLoginProps = Omit<
  LoginContextType,
  | 'handleLogin'
  | 'updateView'
  | 'view'
  | 'user'
  | 'clearLoginState'
  | 'handleEmailResendClicked'
  | 'show'
  | 'onOpen'
  | 'onClose'
> & {
  /** Popup mode (booking widget): the CTA opens the first-party login popup instead of the in-frame OTP. */
  onLoginPopup?: () => void;
  /**
   * Inline mode (a Linked Session): the widget's own OTP login, rendered in
   * place of the field and CTA. There is no popup to open, and no login
   * provider behind `handleEmailSubmit` — the panel brings its own.
   */
  inlineLogin?: ReactNode;
};

const baseTranslationKey = `components.templates.checkout.ProtectedUserLogin`;

const ProtectedUserLogin = ({
  email,
  updateEmail,
  handleEmailSubmit,
  onLoginPopup,
  inlineLogin,
}: ProtectedUserLoginProps) => {
  const { pathname } = useLocation();

  const handleLoginClick = () => {
    if (onLoginPopup) {
      onLoginPopup();
      return;
    }
    handleEmailSubmit({ redirectUrl: pathname });
  };

  useEffect(() => {
    updateEmail(email.value);
    // The inline panel brings its own login provider and opens its own tracking
    // session on mount; force-starting a second one here orphans it.
    if (inlineLogin) return;
    loginTrackingSession.start('checkout_booking_summary_force_login', { force: true });
    loginTrackingSession.track({ step: 'initiated', flow: 'login' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="gap-md flex flex-col">
      <p className="text-black-600">
        {_s(`${baseTranslationKey}.body`)} <span className="text-black-900">{email.value}</span>
      </p>
      {inlineLogin ?? (
        <div className="gap-md flex flex-col">
          <ListInput
            disabled
            id="email"
            type="email"
            value={email.value}
            leftIcon={<Icon variant="email" />}
            error={email.error && email.submitting ? _s(`RequestToken.input.error`) : undefined}
          />
          <Button
            disabled={email.error || email.submitting}
            label={_s(`${baseTranslationKey}.cta`)}
            onClick={handleLoginClick}
            size="md"
            variant="primary"
          />

          <p className="text-black-600 text-m">
            {_s(`RequestToken.terms.text`)}
            <br />
            <a href={TERMS_URLS.USAGE} target="_blank" rel="noreferrer" className="underline">
              {_s(`RequestToken.terms.link`)}
            </a>
          </p>
        </div>
      )}
    </div>
  );
};

export default ProtectedUserLogin;
